import { Injectable } from "@angular/core";
import { Rate } from "../model/Rate";
import { CcyPairLookupService } from "../../lookup/model/impl/CcyPairLookupService";
import {
  BigNumber,
  MarketRateImpl,
  MarketDataEventImpl,
  Product,
  StandardDateImpl,
  Precision,
  CurrencyPairImpl,
  Tenor,
} from "shared/cubiq-model-multi-asset";
import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { RateCache, CURVE_NDF, CURVE_SPT } from "../impl/RateCache";

@Injectable()
export class MarketDataEventTransformer {
  constructor(private ccyPairLookup: CcyPairLookupService, private rateCache: RateCache) {}
  public transformEvent(rateEvent: MarketDataEventImpl): Array<Rate> {
    let rates: Array<Rate> = new Array<Rate>();

    for (let i: number = 0; i < rateEvent.rateData.length; i++) {
      let marketRate: MarketRateImpl = rateEvent.rateData[i] as MarketRateImpl;
      let rate: Rate = this.transformRate(
        rateEvent.subscriptionCode,
        rateEvent.rateSource,
        rateEvent.providerId,
        rateEvent.providerCode,
        marketRate,
      );

      if (rate.tenor === null || rate.valueDate === null) {
        rates.push(rate);
      } else {
        let brokenDateRate = Object.assign(new Rate(), rate, { tenor: null });
        rates.push(rate);
        rates.push(brokenDateRate);
      }
    }

    return rates;
  }

  public transformRate(
    productCode: string,
    source: string,
    providerId: number,
    providerCode: string,
    rate: MarketRateImpl,
  ): Rate {
    let valueDate: Date;
    let date: StandardDateImpl = rate.valueDate as StandardDateImpl;
    if (date) {
      valueDate = new Date(date.year, date.month - 1, date.day);
    }
    let pair = this.ccyPairLookup.getCcyPair(productCode.substr(0, 3), productCode.substr(3, 3));
    if (pair instanceof CurrencyPairImpl) {
      let newRate = new Rate(
        pair,
        source,
        rate.bidPrice ? rate.bidPrice.toString() : null,
        rate.askPrice ? rate.askPrice.toString() : null,
        new Date(),
        rate.isTradable,
        rate.ndfHorizon,
        valueDate,
        rate.tenor,
        providerId,
        providerCode,
        rate.bidSize ? rate.bidSize.toString() : null,
        rate.askSize ? rate.askSize.toString() : null,
      );
      if (Tenor.SPOT == rate.tenor) {
        let ndfFwdRate = this.rateCache.getRateByKey(`${productCode}-${CURVE_NDF}-HORIZON`);
        newRate.marketSpotRateDisplay = this.getSpotMarketDisplayRate(rate, pair);
        newRate.invertMarketSpotRateDisplay = this.getInvertSpotMarketDisplayRate(rate, pair);

        if (!pair.ndfEnabledFg) {
          // non ndf pair
          newRate.marketNdfRateDisplay = newRate.marketSpotRateDisplay;
          newRate.invertMarketNdfRateDisplay = newRate.invertMarketSpotRateDisplay;
          newRate.marketNdfRateTradeable = newRate.tradable;
          newRate.marketNdfPairSpotOnly = true;
        } else if (ndfFwdRate != null) {
          newRate.marketNdfRateDisplay = this.getNdfMarketDisplayRate(newRate, ndfFwdRate, pair);
          newRate.invertMarketNdfRateDisplay = this.getInvertNdfMarketDisplayRate(
            newRate,
            ndfFwdRate,
            pair,
          );
          // newRate.marketNdfRateTradeable = newRate.tradable && ndfFwdRate.tradable;
          newRate.marketNdfRateTradeable = newRate.tradable; //OMS-29117
          newRate.marketNdfPairSpotOnly = false;
        } else {
          newRate.marketNdfRateDisplay = "";
          newRate.invertMarketNdfRateDisplay = "";
          newRate.marketNdfRateTradeable = false;
          newRate.marketNdfPairSpotOnly = false;
        }
      }
      if (rate.ndfHorizon) {
        let spotRate = this.rateCache.getRateByKey(
          `${productCode}-${CURVE_SPT}-${Tenor[Tenor.SPOT]}`,
        );

        if (spotRate != null) {
          newRate.marketSpotRateDisplay = spotRate.marketSpotRateDisplay;
          newRate.invertMarketSpotRateDisplay = spotRate.invertMarketSpotRateDisplay;
          spotRate.marketNdfRateDisplay = newRate.marketNdfRateDisplay =
            this.getNdfMarketDisplayRate(spotRate, newRate, pair);
          spotRate.invertMarketNdfRateDisplay = newRate.invertMarketNdfRateDisplay =
            this.getInvertNdfMarketDisplayRate(spotRate, newRate, pair);
          spotRate.marketNdfRateTradeable = newRate.marketNdfRateTradeable =
            newRate.tradable && spotRate.tradable;
        } else {
          newRate.marketNdfRateTradeable = false;
        }
      }
      if (Tenor.EMPTY == rate.tenor) {
        newRate.tenor = null;
      }

      return newRate;
    }
    // stale and stream off need to be ported over when server piece is done
    logger.out.error(
      `${this.constructor.name}: Error loading currency pair, falling back to default size of 4 decs`,
    );

    return new Rate(
      productCode,
      source,
      rate.bidPrice ? rate.bidPrice.toString() : null,
      rate.askPrice ? rate.askPrice.toString() : null,
      new Date(),
      rate.isTradable,
      rate.ndfHorizon,
      valueDate,
      rate.tenor,
      providerId,
      providerCode,
      rate.bidSize ? rate.bidSize.toString() : null,
      rate.askSize ? rate.askSize.toString() : null,
      4,
      4,
      4,
      4,
    );
  }

  private getSpotMarketDisplayRate(rate: MarketRateImpl, pair: CurrencyPairImpl): string {
    let extraDec: number = pair.spotPrecision == Precision.FULL ? 0 : 1;
    let decs = pair.rateDecimalsQty + extraDec;
    let bidText = rate.bidPrice.toFixed(decs);
    let offerText = rate.askPrice.toFixed(decs);
    if (
      bidText.substring(0, bidText.length - (2 + extraDec)) ==
      offerText.substring(0, offerText.length - (2 + extraDec))
    ) {
      offerText = offerText.substring(offerText.length - (2 + extraDec), offerText.length);
    }
    if (extraDec == 1) {
      bidText =
        bidText.substring(0, bidText.length - 1) +
        "<sup>" +
        bidText.substr(bidText.length - 1, 1) +
        "</sup>";
      offerText =
        offerText.substring(0, offerText.length - 1) +
        "<sup>" +
        offerText.substr(offerText.length - 1, 1) +
        "</sup>";
    }
    return bidText + "/" + offerText;
  }

  private getInvertSpotMarketDisplayRate(rate: MarketRateImpl, pair: CurrencyPairImpl): string {
    let extraDec: number = pair.spotInvertPrecision == Precision.FULL ? 0 : 1;
    let decs = pair.spotInvertDecimalsQty + extraDec;
    // inverted so use oppposite number for bid and offer
    let bidText = new BigNumber(1).dividedBy(rate.askPrice).toFixed(decs);
    let offerText = new BigNumber(1).dividedBy(rate.bidPrice).toFixed(decs);
    if (
      bidText.substring(0, bidText.length - (2 + extraDec)) ==
      offerText.substring(0, offerText.length - (2 + extraDec))
    ) {
      offerText = offerText.substring(offerText.length - (2 + extraDec), offerText.length);
    }
    if (extraDec == 1) {
      bidText =
        bidText.substring(0, bidText.length - 1) +
        "<sup>" +
        bidText.substr(bidText.length - 1, 1) +
        "</sup>";
      offerText =
        offerText.substring(0, offerText.length - 1) +
        "<sup>" +
        offerText.substr(offerText.length - 1, 1) +
        "</sup>";
    }
    return bidText + "/" + offerText;
  }

  private getNdfMarketDisplayRate(
    spotRate: Rate,
    ndfFwdRate: Rate,
    pair: CurrencyPairImpl,
  ): string {
    let extraDec: number = pair.spotPrecision == Precision.FULL ? 0 : 1;
    let decs = pair.fwdRateDecimalsQty + extraDec;
    let spotBidRate = new BigNumber(spotRate.bidRate);
    let spotAskRate = new BigNumber(spotRate.offerRate);
    let ndfBidRate = new BigNumber(ndfFwdRate.bidRate);
    let ndfAskRate = new BigNumber(ndfFwdRate.offerRate);
    let bidText = spotBidRate.plus(ndfBidRate).toFixed(decs);
    let offerText = spotAskRate.plus(ndfAskRate).toFixed(decs);
    if (
      bidText.substring(0, bidText.length - (2 + extraDec)) ==
      offerText.substring(0, offerText.length - (2 + extraDec))
    ) {
      offerText = offerText.substring(offerText.length - (2 + extraDec), offerText.length);
    }
    if (extraDec == 1) {
      bidText =
        bidText.substring(0, bidText.length - 1) +
        "<sup>" +
        bidText.substr(bidText.length - 1, 1) +
        "</sup>";
      offerText =
        offerText.substring(0, offerText.length - 1) +
        "<sup>" +
        offerText.substr(offerText.length - 1, 1) +
        "</sup>";
    }
    return bidText + "/" + offerText;
  }

  private getInvertNdfMarketDisplayRate(
    spotRate: Rate,
    ndfFwdRate: Rate,
    pair: CurrencyPairImpl,
  ): string {
    let extraDec: number = pair.spotInvertPrecision == Precision.FULL ? 0 : 1;
    let decs = pair.fwdInvertDecimalsQty + extraDec;
    let spotBidRate = new BigNumber(spotRate.offerRate);
    let spotAskRate = new BigNumber(spotRate.bidRate);
    let ndfBidRate = new BigNumber(ndfFwdRate.offerRate);
    let ndfAskRate = new BigNumber(ndfFwdRate.bidRate);
    let bidText = new BigNumber(1).dividedBy(spotBidRate.plus(ndfBidRate)).toFixed(decs);
    let offerText = new BigNumber(1).dividedBy(spotAskRate.plus(ndfAskRate)).toFixed(decs);
    if (
      bidText.substring(0, bidText.length - (2 + extraDec)) ==
      offerText.substring(0, offerText.length - (2 + extraDec))
    ) {
      offerText = offerText.substring(offerText.length - (2 + extraDec), offerText.length);
    }
    if (extraDec == 1) {
      bidText =
        bidText.substring(0, bidText.length - 1) +
        "<sup>" +
        bidText.substr(bidText.length - 1, 1) +
        "</sup>";
      offerText =
        offerText.substring(0, offerText.length - 1) +
        "<sup>" +
        offerText.substr(offerText.length - 1, 1) +
        "</sup>";
    }
    return bidText + "/" + offerText;
  }
}
